import { useSyncExternalStore } from 'react'

/**
 * What the invitation page holds outside any one render of it.
 *
 * Three things, each kept here rather than in the page's own state because the
 * page can be unmounted and mounted again while each of them is still true:
 *
 * - **The link.** Read out of the address once and held in this tab's memory,
 *   so the token can leave the address bar and the history entry, and so the
 *   way to the sign-in page and back carries no token at all.
 * - **Refusal seals.** A read the instance refused is not something this
 *   browser may go on showing, and the answer before it is still in the shared
 *   query cache when the page is next drawn.
 * - **Acceptances.** One per session and link, with the key it was sent under,
 *   so an answer lost on the way back is still the same operation after the
 *   page is left and returned to.
 *
 * "Session" is the session era from `documents/held`, the counter the session
 * provider moves on every sign-in, sign-out and refused credential. Not the
 * credentials: the same credentials signed in twice are two sessions, and
 * nothing the first one was shown or sent is the second one's.
 *
 * All of it is memory only. Nothing is written to storage, so a reload, a new
 * tab or a closed browser forgets the token, and the link has to be followed
 * again.
 */

// -- the link ---------------------------------------------------------------

/**
 * How long a link is held without being used.
 *
 * Long enough to sign in, find a second factor and come back; short enough that
 * a laptop left open does not keep somebody's invitation for the afternoon.
 */
export const HOLD_MS = 30 * 60 * 1000

let held: { token: string; until: number; generation: number } | null = null
let holds = 0

/**
 * Hold a link's token for the way to the sign-in page and back.
 *
 * Every hold is a new generation of the link, even of the same token, so a
 * screen that kept an earlier hold cannot let go of a later one.
 */
export function holdLink(token: string, now: number = Date.now()): void {
  holds += 1
  held = token ? { token, until: now + HOLD_MS, generation: holds } : null
}

/** The held token, or nothing once it has run out. */
export function heldLink(now: number = Date.now()): string {
  if (held && held.until <= now) held = null
  return held?.token ?? ''
}

/**
 * Keep the held link for as long as a screen built on it is showing.
 *
 * The accepted screen is drawn from the link, and the address no longer
 * carries it, so a link that ran out or was let go of underneath it would turn
 * that screen into "this address carries no invitation" on the next render.
 * Returns the generation kept, for {@link releaseLink}, or 0 when the held link
 * is not this token.
 */
export function keepLink(token: string): number {
  if (!held || held.token !== token) return 0
  held.until = Number.POSITIVE_INFINITY
  return held.generation
}

/** Let go of the link a screen kept, unless it has been held again since. */
export function releaseLink(generation: number): void {
  if (generation !== 0 && held?.generation === generation) held = null
}

/** The token a fragment carries, or nothing when the address carries none. */
export function tokenFrom(hash: string): string {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  try {
    return decodeURIComponent(raw).trim()
  } catch {
    // A fragment that is not valid percent-encoding is not a token either; the
    // server would refuse it the same way, so it is passed on as it stands.
    return raw.trim()
  }
}

/**
 * The address an invitation's token goes into.
 *
 * Built here so the screen that issues one, and anything that sends it on, do
 * not each learn the shape of the link.
 */
export function invitationLink(origin: string, token: string): string {
  return `${origin}/invitation#${encodeURIComponent(token)}`
}

// -- whose answer it is -------------------------------------------------------

/**
 * One session and one link: what every read, seal and acceptance belongs to.
 *
 * Two sessions are two scopes even when the same credentials opened both: an
 * answer one of them asked for is not shown to the other, and an acceptance one
 * of them sent is not offered to the other to retry.
 */
export function scopeOf(era: number, token: string): string {
  return JSON.stringify([era, token])
}

// -- refusal seals ------------------------------------------------------------

let reads = 0
const seals = new Map<string, number>()

/** A number for a read as it begins, so its answer can be dated. */
export function beginRead(): number {
  reads += 1
  return reads
}

/**
 * The instance refused a read: nothing answered before it is shown again.
 *
 * Sealed at the number of the read that was refused, not at "now", so a read
 * that left before the refusal and came back after it is still behind the seal.
 */
export function seal(scope: string, read: number): void {
  seals.set(scope, Math.max(seals.get(scope) ?? 0, read))
}

/** Whether an answer from this read may be shown: only one begun after any seal. */
export function showable(scope: string, read: number): boolean {
  return read > (seals.get(scope) ?? 0)
}

// -- acceptances ----------------------------------------------------------------

export type Acceptance<R> =
  /** On its way, under `key`. Nothing is known yet. */
  | { state: 'sent'; key: string; retried: boolean }
  /** The instance accepted it; `result` is what it said. */
  | { state: 'done'; key: string; result: R }
  /** No answer came back, or one that says nothing. It may have gone through. */
  | { state: 'uncertain'; key: string; error: unknown }
  /** The instance refused the first send, so nothing was written. */
  | { state: 'refused'; key: string; error: unknown }
  /**
   * The instance refused a retry of a send whose answer had been lost. That
   * says nothing about the first send, which may well have been accepted.
   */
  | { state: 'refused_after_uncertain'; key: string; error: unknown }

const acceptances = new Map<string, Acceptance<unknown>>()
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function acceptanceOf<R>(scope: string): Acceptance<R> | undefined {
  return acceptances.get(scope) as Acceptance<R> | undefined
}

export function setAcceptance<R>(scope: string, next: Acceptance<R> | undefined): void {
  if (next) acceptances.set(scope, next)
  else acceptances.delete(scope)
  for (const listener of listeners) listener()
}

export function useAcceptance<R>(scope: string): Acceptance<R> | undefined {
  return useSyncExternalStore(subscribe, () => acceptanceOf<R>(scope))
}

/** For tests, and nothing else: a fresh tab. */
export function forgetEverything(): void {
  held = null
  holds = 0
  reads = 0
  seals.clear()
  acceptances.clear()
  for (const listener of listeners) listener()
}
